import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, X, Calculator } from "lucide-react";
import { formatMoney } from "@finora/config";
import { CostAllocationMethod, PurchaseCalculationStatus } from "@finora/types";
import { Button } from "@finora/ui";
import { api } from "../../lib/api";

export function CalculationModal({
  goodsReceiptId,
  onClose,
}: {
  goodsReceiptId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [costName, setCostName] = useState("");
  const [costAmount, setCostAmount] = useState("");

  const { data: calc, isLoading } = useQuery({
    queryKey: ["purchase-calculation", goodsReceiptId],
    queryFn: () => api.getPurchaseCalculationByReceipt(goodsReceiptId),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["purchase-calculation", goodsReceiptId] });

  const create = useMutation({
    mutationFn: () => api.createPurchaseCalculation(goodsReceiptId),
    onSuccess: invalidate,
  });

  const addCost = useMutation({
    mutationFn: () => api.addPurchaseCalculationCost(calc!.id, { name: costName, amount: costAmount }),
    onSuccess: () => {
      invalidate();
      setCostName("");
      setCostAmount("");
    },
  });

  const removeCost = useMutation({
    mutationFn: (costId: string) => api.removePurchaseCalculationCost(calc!.id, costId),
    onSuccess: invalidate,
  });

  const setMethod = useMutation({
    mutationFn: (method: CostAllocationMethod) =>
      api.setPurchaseCalculationAllocationMethod(calc!.id, method),
    onSuccess: invalidate,
  });

  const runCalculate = useMutation({
    mutationFn: () => api.calculatePurchaseCalculation(calc!.id),
    onSuccess: invalidate,
  });

  const updatePricing = useMutation({
    mutationFn: ({
      itemId,
      marginPercent,
      sellingPriceGross,
    }: {
      itemId: string;
      marginPercent?: string;
      sellingPriceGross?: string;
    }) => api.updatePurchaseCalculationItemPricing(calc!.id, itemId, { marginPercent, sellingPriceGross }),
    onSuccess: invalidate,
  });

  const confirm = useMutation({
    mutationFn: () => api.confirmPurchaseCalculation(calc!.id),
    onSuccess: invalidate,
  });

  const apply = useMutation({
    mutationFn: () => api.applyPurchaseCalculationToPriceList(calc!.id),
    onSuccess: invalidate,
  });

  const isConfirmed = calc?.status === PurchaseCalculationStatus.CONFIRMED;
  const isLocked = isConfirmed;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-auto rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <Calculator className="h-5 w-5" aria-hidden />
            Kalkulacija nabavne cene
          </h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          {isLoading ? (
            <p className="text-sm text-slate-500">Učitavanje...</p>
          ) : !calc ? (
            <div className="text-center">
              <p className="mb-4 text-sm text-slate-500">
                Za ovaj prijem robe još ne postoji kalkulacija nabavne cene.
              </p>
              <Button onClick={() => create.mutate()} disabled={create.isPending}>
                Kreiraj kalkulaciju
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span
                  className={`rounded-full px-3 py-1 text-xs font-medium ${
                    calc.status === "CONFIRMED"
                      ? "bg-emerald-100 text-emerald-700"
                      : calc.status === "CALCULATED"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {calc.status === "CONFIRMED"
                    ? "Potvrđena"
                    : calc.status === "CALCULATED"
                      ? "Obračunata"
                      : "Nacrt"}
                </span>
                <span className="text-xs text-slate-400">Dobavljač: {calc.supplierName}</span>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-slate-800">Dodatni troškovi nabavke</h3>
                <div className="space-y-1.5">
                  {calc.additionalCosts.map((cost) => (
                    <div
                      key={cost.id}
                      className="flex items-center justify-between rounded-lg bg-app-bg px-3 py-1.5 text-sm"
                    >
                      <span className="text-slate-700">{cost.name}</span>
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{formatMoney(cost.amount)}</span>
                        {!isLocked && (
                          <button
                            onClick={() => removeCost.mutate(cost.id)}
                            className="text-slate-400 hover:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {!isLocked && (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      value={costName}
                      onChange={(e) => setCostName(e.target.value)}
                      placeholder="npr. Transport, Carina, Špedicija..."
                      className="h-9 flex-1 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                    />
                    <input
                      value={costAmount}
                      onChange={(e) => setCostAmount(e.target.value)}
                      placeholder="Iznos"
                      className="h-9 w-28 rounded-lg border border-border px-2 text-right text-sm focus:border-brand focus:outline-none"
                    />
                    <button
                      onClick={() => addCost.mutate()}
                      disabled={!costName || !costAmount || addCost.isPending}
                      className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm text-brand hover:bg-brand-light disabled:opacity-40"
                    >
                      <Plus className="h-3.5 w-3.5" aria-hidden /> Dodaj
                    </button>
                  </div>
                )}
                <div className="mt-2 flex justify-between border-t border-border pt-2 text-sm">
                  <span className="text-slate-500">Ukupno dodatnih troškova</span>
                  <span className="font-semibold">{formatMoney(calc.totalAdditionalCost)}</span>
                </div>
              </div>

              {!isLocked && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Način raspodele troškova
                  </label>
                  <select
                    value={calc.allocationMethod}
                    onChange={(e) => setMethod.mutate(e.target.value as CostAllocationMethod)}
                    className="h-9 w-64 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                  >
                    <option value="BY_VALUE">Proporcionalno nabavnoj vrednosti</option>
                    <option value="BY_QUANTITY">Proporcionalno količini</option>
                  </select>
                  <p className="mt-1 text-xs text-slate-400">
                    Raspodela po težini/zapremini dolazi u sledećoj fazi (treba dodati ta polja na
                    artikal).
                  </p>
                </div>
              )}

              <div className="flex justify-end">
                {calc.status === "DRAFT" && (
                  <Button onClick={() => runCalculate.mutate()} disabled={runCalculate.isPending}>
                    Izračunaj
                  </Button>
                )}
              </div>

              {calc.items.length > 0 && calc.status !== "DRAFT" && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-slate-800">Kalkulacija po artiklu</h3>
                  <table>
                    <thead>
                      <tr>
                        <th>Artikal</th>
                        <th>Kol.</th>
                        <th>Nabavna</th>
                        <th>Dod. trošak</th>
                        <th>Kalkulisana nab.</th>
                        <th>Marža %</th>
                        <th>Prodajna (sa PDV)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calc.items.map((item) => (
                        <tr key={item.id}>
                          <td className="font-medium text-slate-800">
                            {item.name}
                            {!item.hasProduct && (
                              <span className="ml-1 text-xs text-amber-600">(nije povezan artikal)</span>
                            )}
                          </td>
                          <td>{item.quantity}</td>
                          <td>{formatMoney(item.unitPrice)}</td>
                          <td>{formatMoney(item.allocatedCost)}</td>
                          <td className="font-medium">{formatMoney(item.calculatedUnitCost)}</td>
                          <td className="w-20">
                            <input
                              defaultValue={item.marginPercent ?? ""}
                              disabled={isLocked}
                              onBlur={(e) =>
                                e.target.value &&
                                updatePricing.mutate({ itemId: item.id, marginPercent: e.target.value })
                              }
                              className="h-8 w-full rounded border border-border px-1 text-right text-xs focus:border-brand focus:outline-none disabled:bg-app-bg"
                            />
                          </td>
                          <td className="w-28">
                            <input
                              defaultValue={item.sellingPriceGross}
                              disabled={isLocked}
                              onBlur={(e) =>
                                updatePricing.mutate({
                                  itemId: item.id,
                                  sellingPriceGross: e.target.value,
                                })
                              }
                              className="h-8 w-full rounded border border-border px-1 text-right text-xs font-medium focus:border-brand focus:outline-none disabled:bg-app-bg"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="mt-1 text-xs text-slate-400">
                    Unesite maržu (%) ILI direktno prodajnu cenu po stavci - drugo polje se preračunava.
                  </p>
                </div>
              )}

              {apply.data && (
                <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                  Cene primenjene na {apply.data.appliedCount} artikala u cenovniku.
                </p>
              )}
            </>
          )}
        </div>

        {calc && calc.status !== "DRAFT" && (
          <div className="flex gap-3 border-t border-border px-6 py-4">
            {calc.status === "CALCULATED" && (
              <Button className="flex-1" onClick={() => confirm.mutate()} disabled={confirm.isPending}>
                Potvrdi kalkulaciju
              </Button>
            )}
            {isConfirmed && (
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => apply.mutate()}
                disabled={apply.isPending}
              >
                Primeni cene na cenovnik
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
